// src/pages/DevHub/WebhookEcho/WebhookEchoPage.tsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Copy, Trash2, Search, Info, Pause, Play, Forward, X,
    Terminal, Activity, Clock, Send, Check, AlertTriangle, AlertCircle, Loader2
} from 'lucide-react';
import SEO from '../../../components/SEO/SEO';
import {
    DevPageLayout,
    DevPanel,
    DevButton,
} from '../components';

interface WebhookLog {
    id: string;
    method: string;
    path: string;
    url: string;
    headers: Record<string, string>;
    body: any;
    query_params?: Record<string, string>;
    timestamp: number;
    ip: string;
}

type ActiveTab = 'body' | 'headers' | 'query' | 'curl';
type ReplayStatus = 'success' | 'error' | null;

const WebhookDocs: React.FC = () => (
    <div className="mt-[48px] pt-[32px] border-t border-[#dadce0]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[48px]">
            <div>
                <h3 className="text-[18px] font-medium mb-[16px]">How it works</h3>
                <ul className="list-none p-0 text-[#5f6368] text-[14px] leading-loose">
                    <li>1. Generate your unique URL below.</li>
                    <li>2. Paste it into your external service (Stripe, GitHub, etc).</li>
                    <li>3. This dashboard will update live with incoming payloads.</li>
                </ul>
                {/* .dev-alert: flex gap-[12px] p-[16px] bg-[#e8f0fe] text-[#1557b0] rounded-[8px] text-[14px] leading-[1.5] */}
                <div className="flex gap-[12px] p-[16px] bg-[#e8f0fe] text-[#1557b0] rounded-[8px] text-[14px] leading-[1.5] mt-[24px]">
                    <Info size={20} className="flex-shrink-0" />
                    <p><strong>Note:</strong> Logs are stored persistently on disk. Maximum 50 requests are kept per ID.</p>
                </div>
            </div>
            <div>
                <h3 className="text-[18px] font-medium mb-[16px]">🧪 Advanced Mocking</h3>
                <p className="text-[#5f6368] text-[14px] mb-[16px]">Control the response returned using these headers:</p>
                <ul className="list-none p-0 text-[13px] [&_li]:mb-[12px]">
                    <li><code>x-echo-status</code>: Set custom status (e.g. 404)</li>
                    <li><code>x-echo-delay</code>: Artificial delay in ms (max 10s)</li>
                    <li><code>x-echo-body</code>: Custom response body</li>
                </ul>
            </div>
        </div>
    </div>
);

const WebhookEchoPage: React.FC = () => {
    const apiBase = (import.meta as any).env.VITE_API_URL || 'http://localhost:8000';
    const [webhookId, setWebhookId] = useState<string>('');
    const [logs, setLogs] = useState<WebhookLog[]>([]);
    const [activeLog, setActiveLog] = useState<WebhookLog | null>(null);
    const [activeTab, setActiveTab] = useState<ActiveTab>('body');
    const [isPolling, setIsPolling] = useState<boolean>(true);
    const [copied, setCopied] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [replayUrl, setReplayUrl] = useState<string>('http://localhost:5000/webhook');
    const [replaying, setReplaying] = useState<boolean>(false);
    const [replayStatus, setReplayStatus] = useState<ReplayStatus>(null);
    const [showReplayModal, setShowReplayModal] = useState<boolean>(false);

    // Adaptive Polling States
    const [isTabVisible, setIsTabVisible] = useState<boolean>(true);
    const [pollInterval, setPollInterval] = useState<number>(3000);
    const [lastLogCount, setLastLogCount] = useState<number>(0);

    // Test Simulator State
    const [testMethod, setTestMethod] = useState<string>('GET');
    const [testPathSuffix, setTestPathSuffix] = useState<string>('');
    const [testBody, setTestBody] = useState<string>(JSON.stringify({
        message: 'Hello from FileFusion Simulator!',
        timestamp: new Date().toISOString(),
        status: 'success'
    }, null, 2));
    const [testLoading, setTestLoading] = useState<boolean>(false);
    const [testSuccess, setTestSuccess] = useState<boolean>(false);

    const isValidJson = useMemo(() => {
        if (!testBody.trim()) return true;
        try { JSON.parse(testBody); return true; } catch { return false; }
    }, [testBody]);

    const handleReplay = async () => {
        if (!activeLog) return;
        setReplaying(true);
        setReplayStatus(null);
        try {
            const restricted = ['host', 'connection', 'content-length', 'origin', 'referer', 'user-agent',
                'sec-ch-ua', 'accept-encoding', 'x-real-ip', 'x-forwarded-for', 'x-forwarded-proto',
                'x-forwarded-port', 'x-forwarded-host'];
            const cleanHeaders: Record<string, string> = {};
            Object.entries(activeLog.headers).forEach(([k, v]) => {
                if (!restricted.includes(k.toLowerCase())) cleanHeaders[k] = v;
            });
            const res = await fetch(replayUrl, {
                method: activeLog.method,
                headers: { ...cleanHeaders, 'Content-Type': activeLog.headers['content-type'] || 'application/json', 'x-forwarded-by': 'FileFusion-Echo' },
                body: activeLog.method !== 'GET'
                    ? (typeof activeLog.body === 'object' ? JSON.stringify(activeLog.body) : activeLog.body)
                    : undefined
            });
            setReplayStatus(res.ok ? 'success' : 'error');
        } catch (err) {
            console.error('Replay failed:', err);
            setReplayStatus('error');
        } finally {
            setReplaying(false);
            setTimeout(() => setReplayStatus(null), 3000);
        }
    };

    const fetchLogs = useCallback(async () => {
        if (!webhookId) return;
        try {
            const res = await fetch(`${apiBase}/api/webhook-echo/logs/${webhookId}`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            if (Array.isArray(data)) {
                setPollInterval(data.length > lastLogCount ? 3000 : prev => Math.min(prev + 3000, 15000));
                setLastLogCount(data.length);
                setLogs(data);
            }
        } catch (err) {
            console.error('Failed to fetch logs:', err);
        }
    }, [webhookId, lastLogCount, apiBase]);

    useEffect(() => {
        const handleVisibility = () => {
            setIsTabVisible(document.visibilityState === 'visible');
            if (document.visibilityState === 'visible') setPollInterval(3000);
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, []);

    useEffect(() => {
        const id = localStorage.getItem('ff_dev_id') || localStorage.getItem('ff_webhook_id');
        if (id) { setWebhookId(id); if (!localStorage.getItem('ff_dev_id')) localStorage.setItem('ff_dev_id', id); }
    }, []);

    const handleGenerateUrl = () => {
        const id = Math.random().toString(36).substring(2, 12);
        localStorage.setItem('ff_dev_id', id);
        setWebhookId(id);
    };

    const webhookUrl = `${apiBase}/api/webhook-echo/${webhookId}`;

    useEffect(() => { if (webhookId) fetchLogs(); }, [webhookId, fetchLogs]);

    useEffect(() => {
        let interval: NodeJS.Timeout | undefined;
        if (isPolling && isTabVisible && webhookId) interval = setInterval(fetchLogs, pollInterval);
        return () => { if (interval) clearInterval(interval); };
    }, [isPolling, isTabVisible, webhookId, fetchLogs, pollInterval]);

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const clearHistory = async () => {
        if (!window.confirm('Are you sure you want to clear your webhook history?')) return;
        try {
            await fetch(`${apiBase}/api/webhook-echo/logs/${webhookId}`, { method: 'DELETE' });
            setLogs([]); setActiveLog(null);
        } catch (err) { console.error('Failed to clear logs:', err); }
    };

    const formatTime = (ts: number) =>
        new Date(ts * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

    const handleSendTestRequest = async () => {
        setTestLoading(true); setTestSuccess(false);
        try {
            const pathPart = testPathSuffix.startsWith('/') ? testPathSuffix : (testPathSuffix ? '/' + testPathSuffix : '');
            const url = `${webhookUrl}${pathPart}`;
            const options: any = { method: testMethod, headers: { 'Content-Type': 'application/json' } };
            if (testMethod !== 'GET' && testBody) options.body = testBody;
            const res = await fetch(url, options);
            if (res.ok) { setTestSuccess(true); setTimeout(() => setTestSuccess(false), 3000); setTimeout(fetchLogs, 500); }
        } catch (err) { console.error('Test request failed:', err); }
        finally { setTestLoading(false); }
    };

    const generateCurlCommand = (log: WebhookLog | null) => {
        if (!log) return '';
        let cmd = `curl -X ${log.method} "${log.url}"`;
        Object.entries(log.headers).forEach(([k, v]) => {
            if (!['host', 'connection', 'content-length', 'accept-encoding'].includes(k.toLowerCase()))
                cmd += ` \\\n  -H "${k}: ${v}"`;
        });
        if (log.body) {
            const bodyStr = typeof log.body === 'object' ? JSON.stringify(log.body) : String(log.body);
            cmd += ` \\\n  -d '${bodyStr.replace(/'/g, "'\\''")}'`;
        }
        return cmd;
    };

    const getTerminalCurl = () => {
        const pathPart = testPathSuffix.startsWith('/') ? testPathSuffix : (testPathSuffix ? '/' + testPathSuffix : '');
        const fullUrl = `${webhookUrl}${pathPart}`;
        let cmd = `curl -X ${testMethod} "${fullUrl}"`;
        if (testMethod !== 'GET' && testBody) {
            try { cmd += ` -H "Content-Type: application/json" -d '${JSON.stringify(JSON.parse(testBody))}'`; }
            catch { cmd += ` -H "Content-Type: application/json" -d '${testBody}'`; }
        }
        return cmd;
    };

    const filteredLogs = logs
        .filter(log => {
            const q = searchTerm.toLowerCase().trim();
            if (!q) return true;
            return log.method.toLowerCase().includes(q) ||
                log.path.toLowerCase().includes(q) ||
                (log.body && (
                    (typeof log.body === 'object' && JSON.stringify(log.body).toLowerCase().includes(q)) ||
                    (typeof log.body === 'string' && log.body.toLowerCase().includes(q))
                ));
        })
        .sort((a, b) => b.timestamp - a.timestamp);

    // ── Landing screen (no webhook ID yet) ────────────────────────────────
    if (!webhookId) {
        return (
            <DevPageLayout
                title="Webhook Debugger & Echo"
                subtitle="Generate a unique URL to receive and inspect incoming webhook payloads in real-time."
                className="webhook-echo-page"
            >
                <SEO title="Webhook Debugger | FileFusion" description="Real-time webhook debugger. Generate a unique URL to receive and inspect incoming webhook payloads in real-time." />

                {/* .dev-panel .section-spacing → mt-[32px] */}
                <DevPanel className="mt-[32px] text-center py-[80px] px-[24px]">
                    <div className="w-[80px] h-[80px] bg-[#e8f0fe] rounded-[20px] flex items-center justify-center mx-auto mb-[24px] text-[#1a73e8]">
                        <Terminal size={40} />
                    </div>
                    <h2 className="text-[1.75rem] font-medium text-[#202124] mb-[16px]">Your Personal Echo Service</h2>
                    <p className="text-[#5f6368] text-[1.1rem] max-w-[600px] mx-auto mb-[40px] leading-relaxed">
                        FileFusion Webhook Echo provides a real-time, stateless environment to capture, inspect, and replay HTTP requests.
                        Perfect for debugging integration callbacks from Stripe, Github, or your own microservices.
                    </p>
                    <DevButton variant="primary" className="px-[40px] py-[14px] text-[16px] rounded-[12px]" onClick={handleGenerateUrl}>
                        <Activity size={20} /> Create My Unique Endpoint
                    </DevButton>
                </DevPanel>
                <WebhookDocs />
            </DevPageLayout>
        );
    }

    // ── Active dashboard ───────────────────────────────────────────────────
    return (
        <DevPageLayout
            title="Webhook Tester"
            subtitle="Generate a unique URL to receive and inspect incoming webhook payloads in real-time."
            className="webhook-echo-page"
        >
            <SEO title="Webhook Tester | FileFusion" description="Real-time webhook tester. Dispatch, inspect, and debug incoming webhook payloads and simulate events." />

            {/* Test Playground Panel — .dev-panel .section-spacing */}
            <DevPanel className="mt-[32px] shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
                <DevPanel.Header className="!px-[24px] !py-[16px] rounded-t-[16px]">
                    <h3>Webhook Test Playground</h3>
                    <DevButton
                        type="button"
                        variant="primary"
                        className={`min-w-[140px] px-[16px] py-[8px] rounded-[6px] transition-all duration-300 ${testSuccess ? '!bg-[#137333]' : ''}`}
                        onClick={handleSendTestRequest}
                        disabled={testLoading || !isValidJson}
                    >
                        {testLoading ? <><Loader2 size={16} className="animate-spin opacity-50" /> Sending...</>
                            : testSuccess ? <><Check size={16} /> Sent!</>
                                : <><Send size={16} /> Dispatch Request</>}
                    </DevButton>
                </DevPanel.Header>
                <div className="p-[24px]">
                    {/* Method + URL Input */}
                    <div className="flex items-center bg-[#f8f9fa] border border-[#dadce0] rounded-[10px] overflow-hidden mb-[24px] transition-all duration-200 focus-within:bg-white focus-within:border-[#1a73e8] focus-within:shadow-[0_0_0_3px_#e8f0fe] shadow-[inset_0_1px_2px_rgba(0,0,0,0.05)]">
                        <div className="flex rounded-[8px] p-[3px] gap-[2px] mr-[12px]">
                            {['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS'].map(m => (
                                <button key={m}
                                    className={`border-none px-[14px] py-[7px] text-[11px] font-bold rounded-[6px] cursor-pointer transition-all duration-200 uppercase tracking-[0.5px] ${testMethod === m ? 'bg-[#1a73e8] text-white shadow-[0_2px_4px_rgba(0,0,0,0.1)]' : 'bg-transparent text-[#5f6368] hover:bg-black/5 hover:text-[#202124]'}`}
                                    onClick={() => setTestMethod(m)}>{m}</button>
                            ))}
                        </div>
                        <div className="w-[1.5px] h-[24px] bg-[#80868b] mx-[8px] opacity-80" />
                        <div className="flex flex-1 items-center px-[12px] text-[#5f6368] font-mono text-[13px]">
                            <span className="text-[#80868b]">{webhookUrl}/</span>
                            <input
                                className="border-none bg-transparent text-[#202124] font-inherit text-inherit flex-1 outline-none py-[4px] px-0"
                                value={testPathSuffix}
                                onChange={(e) => {
                                    let val = e.target.value;
                                    if (val.startsWith('/')) val = val.substring(1);
                                    setTestPathSuffix(val);
                                }}
                                placeholder="your/custom/path"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-[24px] items-start">
                        {/* Left: Body editor + terminal snippet */}
                        <div className="min-w-0">
                            <div className="flex items-center justify-between mb-[12px]">
                                <span className="text-[11px] font-bold text-[#5f6368] uppercase">
                                    JSON PAYLOAD <span className="font-normal text-[#80868b]">(Browsers typically block payloads for GET)</span>
                                </span>
                                <span className={`text-[10px] px-[6px] py-[2px] rounded-full font-bold uppercase tracking-wide border border-transparent ${!testBody.trim() ? 'bg-[#fef7e0] text-[#ea8600]' : isValidJson ? 'bg-[#e6f4ea] text-[#1e8e3e]' : 'bg-[#fce8e6] text-[#d93025]'}`}>
                                    {!testBody.trim() ? 'EMPTY / NO BODY' : isValidJson ? 'VALID JSON' : 'INVALID FORMAT'}
                                </span>
                            </div>
                            <div className="mb-[24px] rounded-[8px] border border-[#dadce0] overflow-hidden">
                                <textarea
                                    className="w-full min-h-[150px] bg-transparent border-none text-[#202124] font-mono text-[13px] p-[16px] outline-none resize-none"
                                    value={testBody}
                                    onChange={(e) => setTestBody(e.target.value)}
                                    spellCheck="false"
                                />
                            </div>
                            <div className="p-[16px] bg-[#f8f9fa] rounded-[8px] border border-dashed border-[#dadce0]">
                                <div className="flex items-center justify-between gap-[8px] text-[11px] font-bold text-[#80868b] uppercase tracking-[0.5px] mb-[12px]">
                                    <span>{'>'} BEYOND THE PLAYGROUND: TEST FROM TERMINAL</span>
                                    <button
                                        className={`w-[28px] h-[28px] flex items-center justify-center rounded-[6px] cursor-pointer transition-colors duration-200 border-none ${copied ? 'bg-[#e6f4ea] text-[#1e8e3e]' : 'bg-transparent text-[#5f6368] hover:bg-[#e8eaed] hover:text-[#202124]'}`}
                                        onClick={() => copyToClipboard(getTerminalCurl())}
                                        title={copied ? 'Copied!' : 'Copy Snippet'}
                                    >
                                        {copied ? <Check size={14} /> : <Copy size={14} />}
                                    </button>
                                </div>
                                <div className="bg-[#1e1e1e] rounded-[6px] p-[12px] relative flex items-start">
                                    <div className="font-mono text-[12px] text-[#e0e0e0] whitespace-nowrap overflow-x-auto flex-1 pb-[4px] [&::-webkit-scrollbar]:h-[4px] [&::-webkit-scrollbar-thumb]:bg-white/10 [&::-webkit-scrollbar-thumb]:rounded-[4px]">
                                        <code>{getTerminalCurl()}</code>
                                    </div>
                                </div>
                                <p className="mt-[12px] text-[11px] text-[#80868b] leading-[1.4] italic">Postman or curl works best here. Copy this command into your terminal or local dev environment to verify your production-ready endpoint.</p>
                            </div>
                        </div>

                        {/* Right: How to test guide */}
                        <div className="min-w-0 bg-gradient-to-br from-[#ffffff] to-[#f8f9fa] border border-[#dadce0] rounded-[12px] p-[20px] h-full relative overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] before:content-[''] before:absolute before:top-0 before:left-0 before:w-[4px] before:h-full before:bg-[#1a73e8] before:opacity-60">
                            <div className="text-[14px] font-semibold text-[#202124] mb-[12px] flex items-center gap-[8px]">
                                <Info size={16} /> How to test {testMethod}?
                            </div>
                            {[
                                { label: 'OVERVIEW', content: testMethod === 'GET' ? 'Used to fetch data without sending a payload. Perfect for connectivity checks.' : testMethod === 'POST' ? 'Standard for receiving webhook events from external services like Stripe or GitHub.' : 'Used for interacting with resources. Payloads are supported but browser behavior may vary.' },
                                { label: 'REAL-WORLD USE CASE', content: testMethod === 'GET' ? "Testing if your API is 'Alive' or handling simple hooks like Shopify Product Update." : testMethod === 'POST' ? 'Simulating a Stripe Charge Succeeded or GitHub Push event.' : 'Synchronizing resource state across distributed systems.' },
                            ].map(({ label, content }) => (
                                <div className="mb-[16px]" key={label}>
                                    <span className="block text-[10px] font-bold text-[#80868b] uppercase tracking-[0.5px] mb-[4px]">{label}</span>
                                    <p className="text-[13px] text-[#5f6368] leading-[1.5]">{content}</p>
                                </div>
                            ))}
                            <div className="mb-[16px]">
                                <span className="block text-[10px] font-bold text-[#80868b] uppercase tracking-[0.5px] mb-[4px]">NEXT STEPS</span>
                                <ul className="list-none p-0 m-0">
                                    {['Enter an optional path suffix.', <><strong className="text-[#202124]">Dispatch Request</strong>.</>, <>Check the <strong className="text-[#202124]">Recent Requests</strong> list.</>].map((s, i) => (
                                        <li key={i} className="flex gap-[8px] mb-[4px] text-[13px] text-[#5f6368]"><span className="text-[#1a73e8] font-bold">{i + 1}.</span>{s}</li>
                                    ))}
                                </ul>
                            </div>
                            <div className="mb-0">
                                <span className="block text-[10px] font-bold text-[#80868b] uppercase tracking-[0.5px] mb-[4px]">WHAT TO LOOK FOR?</span>
                                <p className="text-[13px] text-[#5f6368] leading-[1.5]">Click the captured log in the sidebar to inspect the <strong className="text-[#202124]">Body</strong>, <strong className="text-[#202124]">Headers</strong>, and <strong className="text-[#202124]">Source IP</strong> in detail.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </DevPanel>

            {/* Unique Endpoint Panel — .dev-panel .section-spacing */}
            <DevPanel className="mt-[32px]">
                <div className="p-[32px]">
                    <h4 className="text-[16px] font-semibold mb-[16px] text-[#202124]">Your Unique Webhook Endpoint</h4>
                    <p className="text-[13px] text-[#5f6368] mb-[20px]">This is a catch-all listener. Any request (GET, POST, PUT, etc) sent here will be captured and echoed.</p>
                    <div className="flex items-stretch bg-white border border-[#dadce0] rounded-[8px] overflow-hidden transition-all duration-200 hover:border-[#1a73e8] hover:shadow-[0_4px_12px_rgba(26,115,232,0.08)]">
                        <div className="bg-[#e8f0fe] text-[#1a73e8] px-[16px] py-[12px] font-bold text-[12px] border-r border-[#dadce0] flex items-center justify-center min-w-[80px]">ANY</div>
                        <div className="flex-[1] px-[16px] py-[12px] font-mono text-[13px] text-[#5f6368] whitespace-pre-wrap break-all flex items-center">{webhookUrl}</div>
                        <div className="flex-1"></div>
                        <button
                            className={`w-[48px] flex items-center justify-center cursor-pointer transition-colors duration-200 border-none ${copied ? 'bg-[#e6f4ea] text-[#1e8e3e]' : 'bg-transparent text-[#5f6368] hover:bg-[#e8eaed] hover:text-[#202124]'}`}
                            onClick={() => copyToClipboard(webhookUrl)}
                            title={copied ? 'Copied!' : 'Copy URL'}
                        >
                            {copied ? <Check size={16} /> : <Copy size={16} />}
                        </button>
                    </div>
                </div>
            </DevPanel>

            {/* Logs + Inspector Grid — .section-spacing → mt-[32px] */}
            <div className="grid grid-cols-1 lg:grid-cols-[350px_1fr] gap-[32px] items-start mt-[32px]">

                {/* Sidebar: Recent Requests — .dev-panel sticky */}
                <DevPanel className="sticky top-[72px]">
                    <div className="px-[16px] h-[48px] border-b border-[#dadce0] flex justify-between items-center bg-[#f8f9fa] rounded-t-[16px]">
                        <h3 className="text-[14px] font-semibold m-0 text-[#202124]">Recent Requests</h3>
                        <div className="flex items-center gap-[4px]">
                            <div className={`text-[9px] font-bold px-[8px] py-[2px] rounded-[4px] border border-transparent ${isPolling ? 'bg-[#e6f4ea] text-[#1e8e3e]' : 'bg-[#fce8e6] text-[#d93025]'}`}>
                                {isPolling ? 'LIVE' : 'PAUSED'}
                            </div>
                            {/* .dev-control-btn: p-[8px] bg-transparent border-0 rounded-[8px] flex items-center justify-center cursor-pointer transition-all */}
                            {/* .dev-control-btn.warning / .active */}
                            <button
                                className={`p-[8px] bg-transparent border-0 rounded-[8px] flex items-center justify-center cursor-pointer transition-all duration-200 focus:outline-none enabled:hover:bg-[#f1f3f4] ${!isPolling ? 'text-[#f9ab00]' : 'text-[#174ea6]'}`}
                                onClick={() => setIsPolling(!isPolling)}
                                title={isPolling ? 'Pause Monitoring' : 'Resume Monitoring'}
                            >
                                {isPolling ? <Pause size={16} /> : <Play size={16} />}
                            </button>
                            {/* .dev-control-btn.danger */}
                            <button
                                className="p-[8px] bg-transparent border-0 rounded-[8px] flex items-center justify-center cursor-pointer transition-all duration-200 focus:outline-none text-[#d93025] enabled:hover:bg-[#f1f3f4]"
                                onClick={clearHistory}
                                title="Clear history"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    </div>
                    <div className="p-[12px_16px] bg-[#f8f9fa] border-b border-[#dadce0]">
                        <div className="bg-white border border-[#dadce0] rounded-[8px] p-[6px_12px] flex items-center gap-[10px] transition-all duration-200 focus-within:border-[#1a73e8] focus-within:shadow-[0_0_0_3px_#e8f0fe]">
                            <Search size={14} className="text-[#80868b] shrink-0" />
                            <input
                                placeholder="Filter requests..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="border-none bg-transparent outline-none w-full text-[13px] text-[#202124] placeholder:text-[#80868b]/80"
                            />
                        </div>
                    </div>
                    <div className="max-h-[calc(100vh-400px)] h-[600px] overflow-y-auto">
                        {filteredLogs.length === 0 ? (
                            /* .dev-empty-state: text-center text-[#54585d] py-[40px] px-[20px] | svg: mb-[16px] opacity-50 */
                            <div className="text-center text-[#54585d] py-[40px] px-[20px]">
                                <Clock size={32} className="mb-[16px] opacity-50 mx-auto" />
                                <p>Waiting for requests...</p>
                            </div>
                        ) : (
                            filteredLogs.map(log => (
                                <button
                                    key={log.id}
                                    className={`w-full p-[12px_16px] border-none border-b border-[#dadce0] rounded-none justify-start text-left cursor-pointer transition-all duration-200 flex items-center ${activeLog?.id === log.id ? 'bg-[#e8f0fe] shadow-[inset_3px_0_0_0_#1a73e8]' : 'bg-transparent hover:bg-[#f8f9fa]'}`}
                                    onClick={() => { setActiveLog(log); setActiveTab('body'); }}
                                >
                                    <div className="flex justify-between w-full items-center gap-[12px]">
                                        <div className="flex gap-[10px] items-center min-w-0 flex-1">
                                            <span className={`text-[9px] font-bold p-[2px_4px] rounded-[4px] uppercase min-w-[36px] text-center ${log.method === 'GET' ? 'bg-[#e8f0fe] text-[#1a73e8]' :
                                                    log.method === 'POST' ? 'bg-[#e6f4ea] text-[#137333]' :
                                                        log.method === 'PUT' ? 'bg-[#fff4e5] text-[#b45309]' :
                                                            log.method === 'PATCH' ? 'bg-[#e0f2f1] text-[#00796b]' :
                                                                log.method === 'DELETE' ? 'bg-[#fce8e6] text-[#a50e0e]' :
                                                                    'bg-[#f3e5f5] text-[#7b1fa2]'
                                                }`}>{log.method}</span>
                                            <span className="text-[13px] font-medium text-[#202124] whitespace-nowrap overflow-hidden text-ellipsis">{log.path}</span>
                                        </div>
                                        <span className="text-[11px] text-[#5f6368] whitespace-nowrap shrink-0">{formatTime(log.timestamp)}</span>
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </DevPanel>

                {/* Inspector Panel — .dev-panel */}
                <DevPanel className="flex flex-col h-[calc(100vh-140px)] min-h-[600px]">
                    <div className="p-[12px_16px] bg-[#f8f9fa] border-b border-[#dadce0] flex justify-between items-center min-h-[52px] shrink-0">
                        <div className="flex gap-[8px] bg-[#f0f2f5] p-[4px] rounded-[10px]">
                            {(['body', 'headers', 'query', 'curl'] as const).map(t => (
                                <button
                                    key={t}
                                    className={`capitalize min-w-[80px] p-[6px_12px] text-[13px] border-none rounded-[8px] font-medium transition-all duration-200 cursor-pointer flex items-center justify-center h-auto w-auto ${activeTab === t ? 'bg-white text-[#1a73e8] shadow-[0_2px_6px_rgba(0,0,0,0.08)]' : 'bg-transparent text-[#5f6368] hover:bg-black/5 hover:text-[#202124]'}`}
                                    onClick={() => setActiveTab(t)}
                                >{t}</button>
                            ))}
                        </div>
                        {activeLog && (
                            <DevButton variant="primary" className="flex items-center gap-[8px] px-[16px] py-[8px] text-[13px] rounded-[8px] transition-transform duration-100 active:scale-95" onClick={() => setShowReplayModal(true)}>
                                <Forward size={14} /> Replay
                            </DevButton>
                        )}
                    </div>
                    <div className="flex-1 flex flex-col bg-white overflow-hidden min-h-0">
                        {activeLog ? (
                            <div className="flex-1 overflow-y-auto p-[12px_20px_20px_20px]">
                                {activeTab === 'body' && (
                                    <div className="m-0 border-none bg-transparent">
                                        <div className="bg-transparent p-[0_0_8px_0] border-none flex justify-between items-center">
                                            <div className="flex items-center gap-[6px] text-[11px] font-bold text-[#202124] uppercase tracking-[0.05em] opacity-90">
                                                <Terminal size={14} /> Raw Body
                                            </div>
                                            <button
                                                className={`w-[28px] h-[28px] flex items-center justify-center rounded-[6px] cursor-pointer transition-colors duration-200 border-none opacity-60 hover:opacity-100 ${copied ? 'bg-[#e6f4ea] text-[#1e8e3e] !opacity-100' : 'bg-transparent text-[#5f6368]'}`}
                                                onClick={() => copyToClipboard(typeof activeLog!.body === 'object' ? JSON.stringify(activeLog!.body, null, 2) : activeLog!.body)}
                                                title={copied ? 'Copied!' : 'Copy Body'}
                                            >
                                                {copied ? <Check size={14} /> : <Copy size={14} />}
                                            </button>
                                        </div>
                                        <pre className="p-0 bg-transparent text-[13px] text-[#202124] overflow-x-auto whitespace-pre-wrap break-words">
                                            <code>{typeof activeLog!.body === 'object' ? JSON.stringify(activeLog!.body, null, 2) : activeLog!.body || '// No body content'}</code>
                                        </pre>
                                    </div>
                                )}
                                {activeTab === 'headers' && (
                                    <table className="w-full text-left border-collapse rounded-[8px] overflow-hidden border border-[#dadce0]">
                                        <thead><tr>
                                            <th className="bg-[#f8f9fa] text-[#80868b] uppercase text-[11px] tracking-[0.05em] p-[12px_16px] font-semibold border-b border-[#dadce0]">Header</th>
                                            <th className="bg-[#f8f9fa] text-[#80868b] uppercase text-[11px] tracking-[0.05em] p-[12px_16px] font-semibold border-b border-[#dadce0]">Value</th>
                                        </tr></thead>
                                        <tbody>
                                            {Object.entries(activeLog!.headers).map(([k, v]) => (
                                                <tr key={k} className="border-b border-[#dadce0] last:border-b-0">
                                                    <td className="p-[12px_16px] text-[13px] align-top">{k}</td>
                                                    <td className="p-[12px_16px] text-[13px] font-mono break-all">{v}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                                {activeTab === 'query' && (
                                    (activeLog!.query_params && Object.keys(activeLog!.query_params).length > 0) ? (
                                        <table className="w-full text-left border-collapse rounded-[8px] overflow-hidden border border-[#dadce0]">
                                            <thead><tr>
                                                <th className="bg-[#f8f9fa] text-[#80868b] uppercase text-[11px] tracking-[0.05em] p-[12px_16px] font-semibold border-b border-[#dadce0]">Parameter</th>
                                                <th className="bg-[#f8f9fa] text-[#80868b] uppercase text-[11px] tracking-[0.05em] p-[12px_16px] font-semibold border-b border-[#dadce0]">Value</th>
                                            </tr></thead>
                                            <tbody>
                                                {Object.entries(activeLog!.query_params!).map(([k, v]) => (
                                                    <tr key={k} className="border-b border-[#dadce0] last:border-b-0">
                                                        <td className="p-[12px_16px] text-[13px] align-top font-mono">{k}</td>
                                                        <td className="p-[12px_16px] text-[13px] font-mono break-all">{v}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <div className="flex-1 flex flex-col items-center justify-center bg-[#fafafa] min-h-[400px] h-full rounded-[8px]">
                                            <div className="p-[48px] text-center max-w-[300px]">
                                                <Search size={40} className="text-black/10 mb-[20px] mx-auto stroke-[1.5px]" />
                                                <p className="text-[#5f6368] text-[16px] font-medium leading-[1.4] mb-[8px]">No query parameters found in this request.</p>
                                            </div>
                                        </div>
                                    )
                                )}
                                {activeTab === 'curl' && (
                                    <div className="m-0 border-none bg-transparent">
                                        <div className="bg-transparent p-[0_0_8px_0] border-none flex justify-between items-center">
                                            <div className="flex items-center gap-[6px] text-[11px] font-bold text-[#202124] uppercase tracking-[0.05em] opacity-90">
                                                <Terminal size={14} /> cURL Command
                                            </div>
                                            <button
                                                className={`w-[28px] h-[28px] flex items-center justify-center rounded-[6px] cursor-pointer transition-colors duration-200 border-none opacity-60 hover:opacity-100 ${copied ? 'bg-[#e6f4ea] text-[#1e8e3e] !opacity-100' : 'bg-transparent text-[#5f6368]'}`}
                                                onClick={() => copyToClipboard(generateCurlCommand(activeLog))}
                                                title={copied ? 'Copied!' : 'Copy cURL'}
                                            >
                                                {copied ? <Check size={14} /> : <Copy size={14} />}
                                            </button>
                                        </div>
                                        <pre className="p-[16px] bg-[#f8f9fa] border border-[#dadce0] rounded-[6px] text-[13px] text-[#202124] whitespace-pre-wrap break-words">
                                            <code>{generateCurlCommand(activeLog)}</code>
                                        </pre>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="flex-1 flex flex-col items-center justify-center bg-[#fafafa] h-full rounded-b-[12px]">
                                <div className="p-[48px] text-center max-w-[300px]">
                                    <Activity size={48} className="text-black/10 mb-[20px] mx-auto stroke-[1.5px]" />
                                    <p className="text-[#5f6368] text-[16px] font-medium leading-[1.4] mb-[8px]">Select a request from the sidebar to inspect its details.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </DevPanel>
            </div>

            <WebhookDocs />

            {/* Replay Modal */}
            {showReplayModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1000]">
                    <div className="bg-white w-[450px] max-w-[90vw] rounded-[12px] shadow-[0_12px_32px_rgba(0,0,0,0.15)] overflow-hidden">
                        <div className="p-[16px_20px] border-b border-[#dadce0] flex justify-between items-center bg-[#f8f9fa]">
                            <h3 className="text-[18px] font-semibold text-[#202124] m-0">Replay to Localhost</h3>
                            <button className="bg-transparent border-none text-[#80868b] cursor-pointer p-[4px] rounded-full flex items-center justify-center transition-all duration-200 hover:bg-[#e8eaed] hover:text-[#202124]" onClick={() => setShowReplayModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-[24px]">
                            {replayStatus === 'success' ? (
                                <div className="text-center py-[20px] animate-[fadeInScale_0.3s_ease-out]">
                                    <div className="w-[64px] h-[64px] bg-[#e6f4ea] text-[#137333] rounded-full flex items-center justify-center mx-auto mb-[16px]">
                                        <Check size={32} />
                                    </div>
                                    <h4 className="text-[20px] text-[#202124] mb-[8px] font-semibold">Replay Successful!</h4>
                                    <p className="text-[#5f6368] text-[14px]">Payload has been forwarded to <code className="bg-[#f8f9fa] p-[2px_6px] rounded-[4px] text-[#1a73e8] border border-[#dadce0]">{replayUrl}</code></p>
                                </div>
                            ) : (
                                <>
                                    <p className="text-[#5f6368] text-[14px] mb-[20px] leading-[1.5]">Forward this payload to your local development environment.</p>
                                    <div className="mt-[16px]">
                                        <label className="block text-[12px] font-semibold mb-[8px] text-[#5f6368] uppercase tracking-[0.5px]">Target URL</label>
                                        <input
                                            type="text"
                                            className="w-full p-[10px_14px] rounded-[8px] border border-[#dadce0] focus:border-[#1a73e8] focus:shadow-[0_0_0_3px_#e8f0fe] outline-none transition-all text-[14px] text-[#202124] font-mono"
                                            value={replayUrl}
                                            onChange={(e) => setReplayUrl(e.target.value)}
                                            placeholder="http://localhost:5000/webhook"
                                        />
                                    </div>
                                    {replayStatus === 'error' && (
                                        <div className="mt-[12px] bg-[#fce8e6] text-[#a50e0e] p-[10px_12px] rounded-[8px] text-[13px] flex items-center gap-[8px] animate-[shake_0.4s_ease-in-out]">
                                            <AlertCircle size={14} className="shrink-0" />
                                            <span>Failed to reach target. Check URL and CORS settings.</span>
                                        </div>
                                    )}
                                    <div className="mt-[20px] p-[12px] bg-[#fff8e1] rounded-[8px] flex gap-[10px] items-start text-[#b45309] text-[12px] leading-[1.4] border border-[#ffe082]">
                                        <AlertTriangle size={16} className="shrink-0 mt-[1px]" />
                                        <span>Target server must support CORS if running in a browser.</span>
                                    </div>
                                </>
                            )}
                        </div>
                        {replayStatus !== 'success' && (
                            <div className="p-[16px_24px] bg-[#f8f9fa] border-t border-[#dadce0] flex justify-end gap-[12px]">
                                <DevButton variant="secondary" onClick={() => setShowReplayModal(false)}>Cancel</DevButton>
                                <DevButton variant="primary" onClick={handleReplay} disabled={replaying}>
                                    {replaying ? 'Sending...' : 'Forward Payload'}
                                </DevButton>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </DevPageLayout>
    );
};

export default WebhookEchoPage;
